<script setup lang="ts">
import { getRepairRequestDocument } from '@/services/rc.service'
import { ElMessage } from 'element-plus'
import { DateTime } from 'luxon'
import { Icon } from '@iconify/vue'

const props = defineProps<{ rrId: string; attachment: any }>()

/**
 * Opens a repair request document in a new tab.
 *
 * @param documentId the ID of the document to open.
 */
const openAttachment = async () => {
  let newTab
  try {
    newTab = window.open()
    if (!newTab) {
      ElMessage({
        type: 'error',
        message: 'Failed to open a new tab, check your browser settings.',
      })
      return
    }
    newTab.document.write('Loading RSR document...')
    const data = await getRepairRequestDocument(props.rrId, props.attachment.id)
    const fileUrl = window.URL.createObjectURL(data)
    newTab.location.assign(fileUrl)
    URL.revokeObjectURL(fileUrl)
  } catch {
    ElMessage({
      type: 'error',
      message: 'Failed to retrieve RSR.',
    })
    newTab?.close()
  }
}
</script>

<template>
  <div class="file" :title="attachment.name" @click="openAttachment">
    <div class="file-icon">
      <Icon class="file-icon" icon="material-symbols:attach-file" :width="24" />
    </div>
    <div class="file-details">
      <span class="filename">RSR &ndash; {{ attachment.name }}</span>
      <small>{{ DateTime.fromISO(attachment.upload_date).toLocaleString(DateTime.DATETIME_SHORT) }}</small>
    </div>
  </div>
</template>

<style scoped>
.file {
  flex-grow: 1;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-spacing-xs);
  padding: var(--spacing-spacing-sm);
  background-color: var(--fill-tertiary);
  border-radius: var(--spacing-spacing-2xs);
  box-shadow: none;
  line-height: normal;
  user-select: none;
  cursor: pointer;
  overflow: hidden;
}

.file-icon {
  color: var(--text-tertiary);
}

.file-details {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-spacing-3xs);
  overflow: hidden;
}

.filename {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
